/**
 * What the secondary button does: grenades and smoke (thrown to the aim point), the LAW, and
 * arming a time bomb (02 §7, 04 §6). Also Cook-off (04 §4): with that perk, holding `use` on
 * a grenade burns its fuse in the hand - release to throw it short-fused, or hold too long.
 */

import { makeNoise } from '../alert';
import type { Ctx } from '../ctx';
import { explode } from '../damage';
import { cos, PI, sin } from '../math';
import { GRENADE, LAW, PERKS, secs } from '../tuning';
import type { Thrown } from '../types';

const DEG = PI / 180;

/** Grenade or smoke, lobbed to the aim point: the same arm, the same reach, half of it lying down. */
function lob(ctx: Ctx, kind: Thrown['kind'], fuseTicks: number): void {
  const { world: w, cmd } = ctx;
  const r = w.ranger;
  const arm = GRENADE.maxRange * w.mods.grenadeRange;
  const max = r.stance === 'crawl' ? arm * 0.5 : arm;
  const range = Math.min(max, Math.max(GRENADE.minRange, cmd.aimDist));
  w.thrown.push({
    id: w.nextId++, kind, fromPlayer: true,
    startX: r.x, startZ: r.z, endX: r.x + sin(r.aim) * range, endZ: r.z + cos(r.aim) * range,
    flightTicks: 0, flightTotal: GRENADE.flightTicks, fuseTicks,
    x: r.x, z: r.z, y: 1.4,
  });
  if (kind === 'smoke') r.gear.smoke--;
  else r.inventory.grenades--;
  r.fireCooldown = secs(0.6);
  ctx.events.push({ type: 'throw', x: r.x, z: r.z });
  r.weapon = 'carbine';
}

/**
 * Cook-off. True while a grenade is burning in his hand: his hands do nothing else. Wounds and
 * staggers do not stop a fuse, so this runs before every other check in `stepWeapons`.
 */
export function stepCookOff(ctx: Ctx): boolean {
  const { world: w, cmd } = ctx;
  const r = w.ranger;
  if (r.throwCharge <= 0) return false;
  r.throwCharge++;
  if (r.throwCharge >= GRENADE.fuseTicks) {
    // Held too long.
    r.throwCharge = 0;
    r.inventory.grenades = Math.max(0, r.inventory.grenades - 1);
    r.fireCooldown = secs(0.6);
    r.weapon = 'carbine';
    explode(ctx, r.x, r.z, GRENADE.radius, 'grenade', true);
  } else if (!cmd.use) {
    const left = GRENADE.fuseTicks - r.throwCharge;
    r.throwCharge = 0;
    lob(ctx, 'grenade', Math.max(1, left));
  }
  return true;
}

/** `viaUse`: the secondary button started this (and may be held, for Cook-off); else it was the trigger. */
export function useHeavy(ctx: Ctx, viaUse: boolean): void {
  const w = ctx.world;
  const r = w.ranger;
  if (r.weapon === 'grenade' && r.inventory.grenades > 0) {
    if (w.mods.cookOff && viaUse) r.throwCharge = 1;
    else lob(ctx, 'grenade', GRENADE.fuseTicks);
  } else if (r.weapon === 'smoke' && r.gear.smoke > 0) {
    lob(ctx, 'smoke', 0);
  } else if (r.weapon === 'law' && r.inventory.laws > 0 && r.stance === 'stand') {
    // A LAW wanders a little off the sights (Bunker Buster's `lawDeviation` 0: never).
    const a = r.aim + (ctx.rng.float() + ctx.rng.float() - 1) * PERKS.lawDeviationDeg * DEG * w.mods.lawDeviation;
    const dirX = sin(a);
    const dirZ = cos(a);
    w.rockets.push({
      id: w.nextId++, x: r.x + dirX * 0.8, z: r.z + dirZ * 0.8, prevX: r.x, prevZ: r.z, dirX, dirZ, travelled: 0,
    });
    r.inventory.laws--;
    r.fireCooldown = LAW.cooldown;
    r.sinceShot = 0;
    ctx.events.push({ type: 'rocket', x: r.x, z: r.z });
    makeNoise(ctx, r.x, r.z, LAW.noise, 'gunfire');
    r.weapon = 'carbine';
  } else if (r.weapon === 'bomb' && r.inventory.bombs > 0) {
    r.action = 'placeBomb';
    r.actionTicks = 0;
  }
}
